import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { listCaseStudies } from "@/lib/api";
import CaseCard from "@/components/CaseCard";
import { CASE } from "@/constants/testIds";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";

// Filtri visivi → set di tipi reali nel DB
const FILTERS = [
  { label: "Tutti", key: "all", match: null },
  { label: "Manager", key: "manager", match: ["Manager"] },
  { label: "Sales · Commerciale", key: "sales", match: ["Sales", "Agenti"] },
  { label: "Tech", key: "tech", match: ["Tech"] },
];

export default function CaseStudies() {
  const [filterKey, setFilterKey] = useState("all");
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    listCaseStudies()
      .then((d) => setAll(d))
      .finally(() => setLoading(false));
  }, []);

  const cases = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filterKey);
    if (!f || !f.match) return all;
    return all.filter((c) => f.match.includes(c.tipo_figura));
  }, [all, filterKey]);

  return (
    <>
      <Seo
        title="Casi studio, Aziende che hanno trovato la figura giusta con Lovac"
        description="Storie reali di aziende italiane che hanno trovato manager, tecnici e profili senior con Lovac. Settori, ruoli, tempi e risultati."
        path="/casi-studio"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Casi studio Lovac",
          url: "https://lovac.it/casi-studio",
          inLanguage: "it-IT",
          breadcrumb: breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Casi studio", path: "/casi-studio" },
          ]),
        }}
      />
      <section className="bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-lovac px-6 pt-24 pb-20 relative z-10">
          <span className="badge-on-dark">Casi studio</span>
          <h1 className="font-display text-5xl sm:text-6xl mt-6 text-on-dark max-w-3xl leading-[1.05]">
            Aziende reali. <span className="text-lovac-accent">Risultati misurabili.</span>
          </h1>
          <p className="text-on-dark-muted text-lg mt-6 max-w-2xl">
            Oltre 300 aziende hanno smesso di aspettare. Ecco alcune delle loro storie.
          </p>
        </div>
      </section>

      <section className="py-24 bg-lovac">
        <div className="container-lovac">
          <div className="flex flex-wrap gap-2 mb-16">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`${CASE.filter}-${f.key}`}
                onClick={() => setFilterKey(f.key)}
                className={`px-5 py-2.5 rounded-full text-sm border-lovac transition-colors ${
                  filterKey === f.key
                    ? "bg-lovac-dark text-on-dark border-transparent"
                    : "bg-lovac hover:bg-lovac-2 text-lovac-dark"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="card-lovac h-80 animate-pulse opacity-60" />
              ))}
            </div>
          ) : cases.length === 0 ? (
            <div className="text-center py-20 text-lovac-muted">
              Nessun caso studio per questo filtro.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {cases.map((c) => (
                <CaseCard key={c.slug} c={c} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA finale */}
      <section className="section bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-narrow text-center relative z-10">
          <span className="badge-on-dark">Prenota una call</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-6 text-on-dark leading-tight">
            Vuoi una mano<br />con la tua ricerca?
          </h2>
          <p className="text-on-dark-muted mt-5 text-base max-w-xl mx-auto">
            Parlacene in una call di 30 minuti. Se non facciamo al caso tuo, te lo diremo.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            <Link to="/contatti" className="btn-primary">
              Prenota una call <ArrowRight size={16} />
            </Link>
            <Link to="/hiring-scan" className="btn-outline-dark">
              Prova l'Hiring Scan <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
